import {defineStore} from "pinia";
import {computed, ref} from "vue";

export type TCartItem = {
  id: number,
  title: string,
  count: number,
  price: number,
  amount: number
}

export type TComment = {
  id: number,
  comment: string
}

export const useCartStore = defineStore('cart', () => {
  const _items = ref({})
  const _favorites = ref<string[]>([])
  const _comments = ref({})
  const _analogs = ref({})

  const comments = computed<Map<string, string>>(() => {
    return new Map(Object.entries(_comments.value))
  })

  const items = computed<Map<string, { count: number, price?: number | string }>>(() => {
    return new Map(Object.entries(_items.value))
  })

  const favorites = computed<Set<string>>(() => {
    return new Set(_favorites.value);
  })

  const analogs = computed<Map<string, string>>(() => {
    return new Map(Object.entries(_analogs.value))
  })

  const isEmpty = computed(() => items.value.size === 0)

  const clearCart = () => {
    items.value.clear()

    _items.value = Object.fromEntries(items.value)
  }
  const favoritesCount = computed(() => favorites.value.size)
  const clearFavorites = () => {
    favorites.value.clear()

    _favorites.value = Array.from(favorites.value.values())
  }

  const incrementItemCount = (productId: number, price?: number | string) => {
    addToCart(productId, price, getItemCountById(productId) + 1)
  }
  const decrementItemCount = (productId: number, price?: number | string) => {
    addToCart(productId, price, getItemCountById(productId) - 1)
  }

  const addToCart = (productId: number, price?: number | string, count = 1) => {
    if (count <= 0) {
      removeFromCart(productId)

      return
    }

    items.value.set(productId.toString(), {count, price})

    _items.value = Object.fromEntries(items.value)
  }
  const removeFromCart = (productId: number) => {
    items.value.delete(productId.toString())

    _items.value = Object.fromEntries(items.value)
  }

  const getItemCountById = (productId: number) => items.value.get(productId.toString())?.count ?? 0
  const getItemsCount = computed(() => items.value.size)
  const isItemExists = (productId: number) => items.value.has(productId.toString()) && getItemCountById(productId) > 0

  const addToFavorite = (productId: number) => {
    if (favorites.value.has(productId.toString())) {
      favorites.value.delete(productId.toString())

      _favorites.value = Array.from(favorites.value.values())

      return
    }

    favorites.value.add(productId.toString())

    _favorites.value = Array.from(favorites.value.values())

    return
  }
  const getCartItemById = (productId: number | string) => {
    return items.value.get(productId.toString())
  }
  const addComment = (_comment: TComment) => {
    const {id, comment} = _comment

    if (comment) {
      comments.value.set(id.toString(), comment)

      _comments.value = Object.fromEntries(comments.value)

      return
    }

    comments.value.delete(id.toString())

    _comments.value = Object.fromEntries(comments.value)
  }

  const addAnalog = (_analog: TComment) => {
    const {id, comment} = _analog

    if (comment) {
      analogs.value.set(id.toString(), comment)

      _analogs.value = Object.fromEntries(analogs.value)

      return
    }

    analogs.value.delete(id.toString())

    _analogs.value = Object.fromEntries(analogs.value)
  }

  const isCommentExists = (productId: number) => {
    return comments.value.has(productId.toString())
  }
  const isFavorite = (productId: number) => {
    return favorites.value.has(productId.toString())
  }
  const areFavoritesExists = computed(() => favorites.value.size > 0)
  const getComment = (productId: number): string => {
    return comments.value.get(productId.toString()) ?? ''
  }

  const getAnalog = (productId: number): string => {
    return analogs.value.get(productId.toString()) ?? ''
  }

  const totalSum = computed(() => {
    return Array.from(items.value.values()).reduce((sum, item) => {
      return sum + (+(item.price || 0) * item.count)
    }, 0)
  })

  const tax = computed(() => {
    return totalSum.value * 0.2
  })

  const getItemTotalSum = (itemId: string | number) => {
    const item = items.value.get(itemId.toString())

    if (typeof item === 'undefined') {
      return
    }

    console.log(item)
    return item.count * (+(item.price || 0))
  }

  return {
    _comments,
    _favorites,
    _items,
    _analogs,
    addComment,
    addToCart,
    addToFavorite,
    areFavoritesExists,
    clearCart,
    clearFavorites,
    comments,
    decrementItemCount,
    favorites,
    favoritesCount,
    getCartItemById,
    getComment,
    getItemCountById,
    getItemsCount,
    getItemTotalSum,
    incrementItemCount,
    isCommentExists,
    isEmpty,
    isFavorite,
    isItemExists,
    items,
    removeFromCart,
    tax,
    totalSum,
    analogs,
    addAnalog,
    getAnalog
  }
}, {
  persist: true
})
